/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useEffect, useState } from 'react';

import * as Sentry from '@sentry/browser';
import { useLazyQuery } from '@apollo/client/react';
import { Separator, Tooltip } from '@opendoor/bricks/complex';

import { ReferralState } from '../__generated__/internalGlobalTypes';
import { GET_LEAD, IGetLead } from '../graphql/queries/GetLead';
import { CustomerCardData, getOfferDetails, Offer, STATUS_MAP } from '../helpers/customerCardData';
import { formatPhoneNumberInput } from '../helpers/formatUtils';
import { deleteRefreshOfferCalledAt, getRefreshOfferCalledAt } from '../helpers/localStorage';
import trackEvent from '../helpers/trackEvent';
import { PURCHASE_AGREEMENT_PAGE_ROUTE } from '../routes/routes';
import styles from '../styles/CustomerCardStyle.less';
import DownloadLink from './DownloadLink';
import HorizontalTimeline from './HorizontalTimeline';
import StatusBadge from './StatusBadge';

interface ICustomerCardProps {
  data: CustomerCardData;
}

const CustomerCard: React.FC<ICustomerCardProps> = (props: ICustomerCardProps) => {
  const { data } = props;
  const [offer, setOffer] = useState<Offer>(data.offer);

  const stateEnum = offer.state;
  const statusColor = STATUS_MAP[offer.status].color;
  const urgent = statusColor === 'red';
  const sellerInputId = data.sellerInputId;
  const config = STATUS_MAP[data.offer.status];

  const getRefreshOfferStartedTime = (): number | null => {
    return offer.status === ReferralState.OFFER_EXPIRED
      ? getRefreshOfferCalledAt(sellerInputId)
      : null;
  };
  const [refreshOfferStartedTime, setRefreshOfferStartedTime] = useState<number | null>(
    getRefreshOfferStartedTime,
  );
  const [numCallsToGetLead, setNumCallsToGetLead] = useState<number>(0);
  const [refreshOfferErrored, setRefreshOfferErrored] = useState<boolean>(false);

  const [getLead] = useLazyQuery<IGetLead>(GET_LEAD, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data) {
        const loadedOfferState = data!.lead!.offer!.state;
        if (loadedOfferState === 'sent') {
          const newOffer = getOfferDetails(data);
          if (newOffer) {
            setOffer(newOffer);
            deleteRefreshOfferCalledAt(sellerInputId);
            setRefreshOfferStartedTime(null);
            setNumCallsToGetLead(0);
          }
        }
      }
      setNumCallsToGetLead(numCallsToGetLead + 1);
    },
    onError: (err) => {
      setNumCallsToGetLead(numCallsToGetLead + 1);
      setRefreshOfferErrored(true);
      Sentry.withScope((scope) => {
        scope.setExtra('seller_input_uuid', sellerInputId);
        scope.setExtra('error', JSON.stringify(err));
        Sentry.captureException(err);
      });
    },
  });

  useEffect(() => {
    if (refreshOfferStartedTime && !refreshOfferErrored) {
      // Offer refresh takes at most 60 seconds to update web.
      if (Date.now() - refreshOfferStartedTime <= 700000) {
        getLead({
          variables: { sellerInputId },
        });
      } else {
        setRefreshOfferErrored(true);
      }
    }
    // eslint-disable-next-line
  }, [numCallsToGetLead]);

  return (
    <div>
      <div className={styles['top-info-container']}>
        <div className={styles['top-info-container-left']}>
          <div className={styles['customer-info-container']}>
            <div className={styles.name}>{data.customer.fullName}</div>
            <div className={styles.address}>{data.customer.street}</div>
          </div>
        </div>
        <div className={styles['top-info-container-right']}>
          <StatusBadge status={config.text} color={config.color} />
        </div>
      </div>
      {data.customer.email || data.customer.phoneNumber ? (
        <div
          className={`${styles['phone-email-container']} ${
            offer ? (!offer.cepName ? 'small-margin-bottom' : null) : 'large-margin-bottom'
          }`}
        >
          {data.customer.email ? <div>{data.customer.email}</div> : null}
          {data.customer.email && data.customer.phoneNumber ? (
            <div className={styles['phone-email-separator']}>•</div>
          ) : null}
          {data.customer.phoneNumber ? (
            <div>{formatPhoneNumberInput(data.customer.phoneNumber)}</div>
          ) : null}
        </div>
      ) : null}
      {offer.cepName && (
        <div
          className={`${styles['phone-email-container']}  ${
            offer ? 'small-margin-bottom' : 'large-margin-bottom'
          }`}
        >
          <span>
            Opendoor Contact
            <Tooltip
              analyticsName="partnership-dashboard-fe-opendoor-contact-tooltip"
              label="What is an Opendoor Contact?"
            >
              Your customer has been assigned to this contact. Please have your customer reach out
              to {offer.cepName} if they have any questions about their offer or sale process.
            </Tooltip>
            :&nbsp; {offer.cepName}
          </span>
        </div>
      )}
      {stateEnum > 2 ? (
        <div className="medium-margin-bottom">
          <div className={`${styles['download-link']} small-margin-bottom`}>
            <DownloadLink
              href={PURCHASE_AGREEMENT_PAGE_ROUTE(data.customer.referralUuid)}
              onClick={trackEvent.bind(null, 'click', 'purchase-agreement')}
            >
              Download contract (PDF)
            </DownloadLink>
          </div>
          <Separator />
        </div>
      ) : null}
      <HorizontalTimeline
        steps={[
          {
            label: 'OFFER',
            isStepCompleted: stateEnum > 0,
          },
          {
            label: 'ASSESSMENT',
            isStepCompleted: stateEnum > 1,
          },
          {
            label: 'SIGNED',
            isStepCompleted: stateEnum > 2,
          },
          {
            label: 'CLOSED',
            isStepCompleted: stateEnum > 3,
          },
        ]}
        style={urgent ? 'urgent' : undefined}
      />
      <div className={`${styles['journey-details']} large-margin-top small-margin-bottom`}>
        {offer.offerData.map((details, index) => (
          <div className={styles['journey-detail-container']} key={index}>
            <div className={styles['journey-detail-label']}>{details.label}</div>
            <div className={styles['journey-detail-value']}>{details.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CustomerCard;
